import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Button
} from '@mui/material';
import {
  LocalHospital as ChemoIcon,
  Waves as RadiationIcon,
  ContentCut as SurgeryIcon,
  Flag as StartIcon,
  CheckCircle as CompleteIcon,
  Timeline as TimelineIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getTreatmentPlans } from '../../services/oncologyService';
import { getChemotherapyCycles } from '../../services/chemotherapyService';
import { PageHeader, ContentCard } from './oncologyStyles';

const TreatmentTimeline = ({ treatmentPlan: propTreatmentPlan, chemoCycles: propChemoCycles, onCancel, embedded = false }) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  
  const [treatmentPlan, setTreatmentPlan] = useState(propTreatmentPlan || null);
  const [chemoCycles, setChemoCycles] = useState(propChemoCycles || []);
  const [loading, setLoading] = useState(!propTreatmentPlan && !!patientId);
  const [error, setError] = useState(null);

  // Fetch data when used as a route (no props provided)
  useEffect(() => {
    const fetchData = async () => {
      if (propTreatmentPlan || !patientId || !defaultFacility?.id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch active treatment plans for the patient
        const plansResponse = await getTreatmentPlans(patientId, defaultFacility.id, { status: 'Active' });
        const plans = plansResponse?.data || [];
        
        if (plans.length > 0) {
          const activePlan = plans[0]; // Use the most recent active plan
          setTreatmentPlan(activePlan);
          
          // Fetch chemotherapy cycles for this patient
          const cyclesResponse = await getChemotherapyCycles(patientId, defaultFacility.id);
          setChemoCycles(cyclesResponse?.data || []);
        }
      } catch (err) {
        console.error('Error fetching treatment timeline:', err);
        setError('Failed to load treatment timeline');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, defaultFacility?.id, propTreatmentPlan]);

  // Update state when props change
  useEffect(() => {
    if (propTreatmentPlan) {
      setTreatmentPlan(propTreatmentPlan);
    }
    if (propChemoCycles) {
      setChemoCycles(propChemoCycles);
    }
  }, [propTreatmentPlan, propChemoCycles]);

  // Wrapper component for standalone vs embedded modes
  const renderWrapper = (content) => {
    if (embedded) {
      return (
        <ContentCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={() => onCancel ? onCancel() : navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <TimelineIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Treatment Timeline
              </Typography>
            </Box>
            {content}
          </CardContent>
        </ContentCard>
      );
    }

    return (
      <Box>
        <PageHeader
          title="Treatment Timeline"
          subtitle="Visual treatment progress and milestones"
          icon={<TimelineIcon />}
          backTo="/oncology"
        />
        <ContentCard>
          <CardContent>
            {content}
          </CardContent>
        </ContentCard>
      </Box>
    );
  };

  if (loading) {
    return renderWrapper(
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return renderWrapper(
      <Alert severity="error">{error}</Alert>
    );
  }

  // No patient or treatment plan (standalone mode)
  if (!patientId && !embedded) {
    return (
      <Box>
        <PageHeader
          title="Treatment Timeline"
          subtitle="Visual treatment progress and milestones"
          icon={<TimelineIcon />}
          backTo="/oncology"
        />
        <ContentCard>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please select a patient to view their treatment timeline.
            </Alert>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              To view a treatment timeline, navigate to a patient's profile and use the Oncology tab.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/patients')}>
              Go to Patients
            </Button>
          </CardContent>
        </ContentCard>
      </Box>
    );
  }

  if (!treatmentPlan) {
    return renderWrapper(
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No treatment plan found for this patient.
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(`/oncology/treatment-plan/new/${patientId}`)}>
          Create Treatment Plan
        </Button>
      </Box>
    );
  }

  // Build timeline events
  const events = [];

  // Plan start
  if (treatmentPlan.plannedStartDate) {
    events.push({
      date: treatmentPlan.plannedStartDate,
      type: 'start',
      title: 'Treatment Plan Start',
      description: treatmentPlan.planName,
      icon: <StartIcon />,
      color: 'primary.main'
    });
  }

  // Chemotherapy cycles
  chemoCycles.forEach(cycle => {
    const colorMap = {
      'Completed': 'success.main',
      'In Progress': 'warning.main',
      'default': 'grey.500'
    };
    events.push({
      date: cycle.actualStartDate || cycle.plannedStartDate,
      type: 'chemo',
      title: `Chemotherapy Cycle ${cycle.cycleNumber}`,
      description: cycle.status,
      icon: <ChemoIcon />,
      color: colorMap[cycle.status] || colorMap.default
    });
  });

  // Surgery
  if (treatmentPlan.surgery?.plannedDate) {
    events.push({
      date: treatmentPlan.surgery.plannedDate,
      type: 'surgery',
      title: 'Surgery',
      description: treatmentPlan.surgery.procedureName,
      icon: <SurgeryIcon />,
      color: 'error.main'
    });
  }

  // Radiation
  if (treatmentPlan.radiation?.startDate) {
    events.push({
      date: treatmentPlan.radiation.startDate,
      type: 'radiation',
      title: 'Radiation Therapy Start',
      description: `${treatmentPlan.radiation.technique} - ${treatmentPlan.radiation.totalDoseGy} Gy in ${treatmentPlan.radiation.fractions} fractions`,
      icon: <RadiationIcon />,
      color: 'info.main'
    });
  }

  // Plan end
  if (treatmentPlan.estimatedEndDate) {
    events.push({
      date: treatmentPlan.estimatedEndDate,
      type: 'end',
      title: 'Estimated Treatment End',
      description: '',
      icon: <CompleteIcon />,
      color: 'success.main'
    });
  }

  // Sort by date
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Timeline content
  const timelineContent = (
    <>
      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No timeline events to display.
        </Typography>
      ) : (
        <Box sx={{ position: 'relative', pl: 4 }}>
          {/* Vertical line */}
          <Box
            sx={{
              position: 'absolute',
              left: 20,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: 'grey.300',
              zIndex: 0
            }}
          />
          
          {events.map((event, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 3,
                position: 'relative',
                '&:last-child': { mb: 0 }
              }}
            >
              {/* Timeline dot */}
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: event.color,
                  position: 'absolute',
                  left: 0,
                  zIndex: 1
                }}
              >
                {event.icon}
              </Avatar>
              
              {/* Content card */}
              <Card
                sx={{
                  ml: 7,
                  flex: 1,
                  boxShadow: 1,
                  '&:hover': { boxShadow: 3 },
                  transition: 'box-shadow 0.2s'
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {event.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(event.date)}
                    </Typography>
                  </Box>
                  {event.description && (
                    <Typography variant="body2" color="text.secondary">
                      {event.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </>
  );

  return renderWrapper(timelineContent);
};

export default TreatmentTimeline;
